import { useMemo } from 'react'

import { filterStoreRepo } from 'components/Filters/FilterStore'
import FilterModal, {
  FilterGroup,
} from 'components/Filters/components/FilterModal'
import { DateRangeFilter } from 'components/Filters/fields/DateRangeFilter'
import { FilterIdentifier } from 'models/saved_view'
import { SelectFilter } from 'components/Filters/fields/SelectFilter'
import {
  useGetProductsCatalog,
  useGetSyndigoCategories,
  useGetSyndigoBrands,
} from 'services/products_api'
import { featureFlagService } from 'utils/featureFlagService'

export default function VariantsFilterset({
  filterIdentifier,
}: {
  filterIdentifier: FilterIdentifier
}) {
  const variantsFilterStore = filterStoreRepo.getStore(
    filterIdentifier as FilterIdentifier
  )

  const { enableSyndigoIntegration } = featureFlagService()

  // Fetch products for the product filter dropdown
  const { data: productsData } = useGetProductsCatalog({
    page: 1,
    page_size: 1000, // Get all products for filter
    sort: 'name', // Sort alphabetically
  })

  // Fetch Syndigo categories and brands when feature flag is enabled
  const { data: categoriesData } = useGetSyndigoCategories(
    enableSyndigoIntegration
  )
  const { data: brandsData } = useGetSyndigoBrands(enableSyndigoIntegration)

  // Convert products to filter options
  const productOptions = useMemo(() => {
    if (!productsData?.results) return []
    return productsData.results.map((product: any) => ({
      label: product.name,
      value: product.id.toString(),
    }))
  }, [productsData])

  // Convert Syndigo categories to filter options
  const categoryOptions = useMemo(() => {
    if (!categoriesData) return []
    return categoriesData
  }, [categoriesData])

  // Convert Syndigo brands to filter options
  const brandOptions = useMemo(() => {
    if (!brandsData) return []
    return brandsData
  }, [brandsData])

  return (
    <FilterModal store={variantsFilterStore} identifier={filterIdentifier}>
      <FilterGroup title="Variant Status">
        <SelectFilter
          filterKey="match_status"
          title="Product Link Status"
          filterStore={variantsFilterStore}
          options={[
            { label: 'All', value: 'all' },
            { label: 'Has Product', value: 'matched' },
            { label: 'No Product', value: 'unmatched' },
          ]}
        />
      </FilterGroup>
      <FilterGroup title="Product">
        <SelectFilter
          filterKey="product"
          title="Filter by Product"
          filterStore={variantsFilterStore}
          options={productOptions}
        />
      </FilterGroup>
      <FilterGroup title="Date Filters">
        <DateRangeFilter
          filterKey="created"
          title="Created Date"
          filterStore={variantsFilterStore}
        />
        <DateRangeFilter
          filterKey="modified"
          title="Modified Date"
          filterStore={variantsFilterStore}
        />
      </FilterGroup>
      {enableSyndigoIntegration && (
        <FilterGroup title="Syndigo Attributes">
          <SelectFilter
            filterKey="source"
            title="Source"
            filterStore={variantsFilterStore}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Syndigo', value: 'syndigo' },
              { label: 'Firstbite', value: 'firstbite' },
            ]}
          />
          <SelectFilter
            filterKey="category"
            title="Category"
            filterStore={variantsFilterStore}
            options={categoryOptions}
          />
          <SelectFilter
            filterKey="brand"
            title="Brand"
            filterStore={variantsFilterStore}
            options={brandOptions}
          />
          <DateRangeFilter
            filterKey="last_synced_at"
            title="Last Synced Date"
            filterStore={variantsFilterStore}
          />
        </FilterGroup>
      )}
    </FilterModal>
  )
}
